import { titleize } from 'inflected';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from '@emotion/styled';

import { EditTagInput } from './styled';
import { addStoryTag, getStory } from '../../../stories/api';
import { postCollectionTag, getCollection } from '../../api';
import { useEntityActionCreators } from '../../../entities/hooks';
import { useValue } from '../../../app/hooks';
import jsonApiReferenceShape from '../../../app/shapes/json-api-reference-shape';
import { Button, Flex, Box } from 'rebass';

const NewTag = ({ setNewTagDirty, chapters, collection, storyId, onCreate, onSubmit }) => {
  const [value, set, reset] = useValue('');
  const [submitting, setSubmitting] = useState(false);
  const { receive } = useEntityActionCreators();

  const tagNames = (chapters||[]).map(ch => ch.name.toLowerCase())

  if (value === '') {
    setNewTagDirty(false);
  } else {
    setNewTagDirty(true);
  }

  const handleSubmit = async () => {
    if (storyId) {
      await addStoryTag({ journalId: collection.id, storyId, name: value });
      await getStory(storyId).then(payload => receive({ payload }));
      await getCollection(collection).then(payload => receive({ payload }));
      onCreate(titleize(value));
      reset();
      setSubmitting(false);
      return;
    }

    postCollectionTag({ ...collection, attributes: { name: value } })
      .then(() => getCollection(collection))
      .then(async payload => {
        receive({ payload });
        if (storyId) {
          const storyRes = await getStory(storyId);
          receive({ payload: storyRes });
        }
        reset();
        setSubmitting(false);
      });
  };

  return (
    <AddTagWrap
      onSubmit={async e => {
        e.preventDefault();
        setSubmitting(true);
        if (onSubmit) {
          onSubmit(value);
          reset();
          setSubmitting(false);
        }
        else
          handleSubmit();
      }}
    >
      <EditTagInput
        type="text"
        value={value}
        onChange={e => set(e.target.value)}
        placeholder="New chapter"
      />
      {value &&
        <Flex justifyContent="space-between" >
          <Button
            type="button"
            variant="muted"
            onClick={() => set('')}
            bg="white"
            color="#8F8F8F"
            justifyContent="center"
            alignItems="center"
            sx={{ borderRadius: '23px', border: '1px solid #8F8F8F' }}
            width={120}
            height={44}
          >
            Cancel
          </Button>
          {tagNames === value.toLowerCase() && <p>Chapter already exists</p>}
          {!tagNames.includes(value) && (
            <Button type="submit" variant="primary"
              bg="#9B59B6"
              width={120}
              height={44}
              justifyContent="center"
              alignItems="center"
              sx={{ borderRadius: '23px', border: 0 }}
              disabled={submitting || tagNames === value.toLowerCase() || !value}
            >
              Save
            </Button>
          )}
        </Flex>}
      <Box sx={{ borderTop: '1px solid #E2E8F0' }} mt="20px" />
    </AddTagWrap>
  );
};

NewTag.propTypes = {
  setNewTagDirty: PropTypes.string,
  collection: jsonApiReferenceShape.isRequired,
  storyId: PropTypes.string,
  onCreate: PropTypes.func,
};

NewTag.defaultProps = {
  onCreate: () => { },
  storyId: '',
  setNewTagDirty: () => { },
};

const AddTagWrap = styled.form`
  display: block;
  margin-bottom: 10px;
  input {
    font-size: 16px;
    margin-right: auto;
    border: none;
    width: 100%;
    outline: none;
  }
  button {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
    transition: opacity 0.175s ease;
    &:disabled {
      opacity: 0.5;
    }
  }
  p {
    color: ${props => props.theme.colors.red};
    margin-right: 4px;
  }
  span {
    font-size: 14px;
    width: 53px;
    color: ${props => props.theme.colors.brand};
  }
`;

export default NewTag;
